import { Injectable, inject } from '@angular/core';
import { Observable, finalize, tap } from 'rxjs';

import { AuthService } from '@core/services/auth.service';

import { AuthSession } from '../../models/auth-session.model';
import { LoginRequest, SocialLoginRequest } from '../../models/login.dto';
import { AuthRepositoryContract } from '../repositories/auth.repository.contract';
import { AuthStore } from '../store/auth.store';
import { apiErrorMessage } from './api-error-message';

@Injectable()
export class LoginUseCase {
  private readonly repository = inject(AuthRepositoryContract);
  private readonly store = inject(AuthStore);
  private readonly auth = inject(AuthService);

  /**
   * Returns the stream rather than subscribing internally, so the page decides
   * when to navigate and can keep its own in-flight flag in step with the
   * request. The store is still updated here, so any other view observing auth
   * state stays correct.
   */
  execute(body: LoginRequest): Observable<AuthSession> {
    return this.establish(this.repository.login(body));
  }

  /**
   * Same outcome as `execute`, from a profile the client obtained through the
   * provider's own sign-in flow (see SocialLoginRequest).
   */
  executeSocial(body: SocialLoginRequest): Observable<AuthSession> {
    return this.establish(this.repository.socialLogin(body));
  }

  private establish(request: Observable<AuthSession>): Observable<AuthSession> {
    this.store.setLoading(true);
    this.store.setError(null);

    return request.pipe(
      tap({
        next: (session) => {
          // setSession is the only way a token enters the app, so storage and
          // the in-memory signal cannot drift apart.
          this.auth.setSession(session.token, session.user);
          this.store.setUser(session.user ?? null);
        },
        error: (err: unknown) => {
          this.store.setError(
            apiErrorMessage(err, 'We could not sign you in. Please check your details and try again.'),
          );
        },
      }),
      finalize(() => this.store.setLoading(false)),
    );
  }
}
